// Production-Ready Briefing Document Viewer
// Features: Table of contents, importance-coded findings, expandable sections, export, copy

import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  FileText,
  FileImage,
  Hash,
  ListChecks,
  ScrollText,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { BriefingDoc } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

interface BriefingDocViewerProps {
  briefingDoc: BriefingDoc;
  className?: string;
}

interface SectionLink {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const IMPORTANCE_CONFIG: Record<
  'critical' | 'high' | 'medium' | 'low',
  { badge: 'destructive' | 'default' | 'secondary' | 'outline'; border: string; label: string }
> = {
  critical: {
    badge: 'destructive',
    border: 'border-l-4 border-l-red-500',
    label: 'Critical',
  },
  high: {
    badge: 'default',
    border: 'border-l-4 border-l-amber-500',
    label: 'High',
  },
  medium: {
    badge: 'secondary',
    border: 'border-l-4 border-l-blue-500',
    label: 'Medium',
  },
  low: {
    badge: 'outline',
    border: '',
    label: 'Low',
  },
};

export function BriefingDocViewer({ briefingDoc, className = '' }: BriefingDocViewerProps) {
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(new Set());
  const [expandedAppendices, setExpandedAppendices] = useState<Set<number>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);

  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();

  // Build table of contents sections
  const tocSections = useMemo<SectionLink[]>(() => {
    const sections: SectionLink[] = [
      { id: 'executive-summary', label: 'Executive Summary', icon: <Briefcase className="h-3.5 w-3.5" /> },
      { id: 'background', label: 'Background', icon: <ScrollText className="h-3.5 w-3.5" /> },
      { id: 'key-findings', label: 'Key Findings', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
      { id: 'recommendations', label: 'Recommendations', icon: <ListChecks className="h-3.5 w-3.5" /> },
    ];
    if (briefingDoc.nextSteps?.length) {
      sections.push({ id: 'next-steps', label: 'Next Steps', icon: <ArrowRight className="h-3.5 w-3.5" /> });
    }
    if (briefingDoc.appendix?.length) {
      sections.push({ id: 'appendix', label: 'Appendix', icon: <FileText className="h-3.5 w-3.5" /> });
    }
    return sections;
  }, [briefingDoc.nextSteps, briefingDoc.appendix]);

  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const toggleFinding = (index: number) => {
    setExpandedFindings((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleAppendix = (index: number) => {
    setExpandedAppendices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const copyAllText = useCallback(() => {
    const parts: string[] = [];
    parts.push(briefingDoc.title);
    parts.push(`Date: ${briefingDoc.date}`);
    if (briefingDoc.preparedFor) {
      parts.push(`Prepared for: ${briefingDoc.preparedFor}`);
    }
    parts.push('');
    parts.push('EXECUTIVE SUMMARY');
    parts.push(briefingDoc.executiveSummary);
    parts.push('');
    parts.push('BACKGROUND');
    parts.push(briefingDoc.background);
    parts.push('');
    parts.push('KEY FINDINGS');
    briefingDoc.keyFindings.forEach((f, i) => {
      parts.push(`${i + 1}. [${f.importance.toUpperCase()}] ${f.finding}`);
      if (f.details) parts.push(`   ${f.details}`);
    });
    parts.push('');
    parts.push('RECOMMENDATIONS');
    briefingDoc.recommendations.forEach((r, i) => {
      parts.push(`${i + 1}. ${r}`);
    });
    if (briefingDoc.nextSteps?.length) {
      parts.push('');
      parts.push('NEXT STEPS');
      briefingDoc.nextSteps.forEach((s, i) => {
        parts.push(`${i + 1}. ${s}`);
      });
    }
    if (briefingDoc.appendix?.length) {
      parts.push('');
      parts.push('APPENDIX');
      briefingDoc.appendix.forEach((a) => {
        parts.push(`--- ${a.title} ---`);
        parts.push(a.content);
      });
    }
    navigator.clipboard.writeText(parts.join('\n')).then(() => {
      toast.success('Document text copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy text');
    });
  }, [briefingDoc]);

  // Stats
  const findingsCount = briefingDoc.keyFindings?.length ?? 0;
  const recommendationsCount = briefingDoc.recommendations?.length ?? 0;
  const nextStepsCount = briefingDoc.nextSteps?.length ?? 0;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Document Header */}
      <div className="relative overflow-hidden rounded-lg border bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-900 dark:to-slate-800/50 p-5 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
              {briefingDoc.title}
            </h2>
            <div className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                {briefingDoc.date}
              </span>
              {briefingDoc.preparedFor && (
                <span className="flex items-center gap-1.5">
                  <Briefcase className="h-3.5 w-3.5" />
                  Prepared for: {briefingDoc.preparedFor}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={copyAllText}>
              <Copy className="h-4 w-4 mr-1.5" />
              Copy All
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting}>
                  <Download className="h-4 w-4 mr-1.5" />
                  {isExporting ? 'Exporting…' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportToPdf('briefing-doc', briefingDoc)}>
                  <FileImage className="h-4 w-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToDocx('briefing-doc', briefingDoc)}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export as Word (.docx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <Separator className="mt-4" />
      </div>

      {/* Table of Contents (visible on all screens at top) */}
      <Card className="print:hidden">
        <CardContent className="p-3 sm:p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Contents
          </p>
          <div className="flex flex-wrap gap-1.5">
            {tocSections.map((section) => (
              <Button
                key={section.id}
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1.5"
                onClick={() => scrollToSection(section.id)}
              >
                {section.icon}
                {section.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{findingsCount}</p>
            <p className="text-[11px] text-muted-foreground">
              Finding{findingsCount !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{recommendationsCount}</p>
            <p className="text-[11px] text-muted-foreground">
              Recommendation{recommendationsCount !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{nextStepsCount}</p>
            <p className="text-[11px] text-muted-foreground">
              Next Step{nextStepsCount !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Document Content */}
      <ScrollArea className="max-h-[700px]">
        <div ref={contentRef} className="flex flex-col gap-6 pr-2">
          {/* Executive Summary */}
          <section id="executive-summary">
            <Card className="overflow-hidden bg-gradient-to-br from-teal-50 to-cyan-50/50 dark:from-teal-950/30 dark:to-cyan-950/20 border-teal-200/50 dark:border-teal-800/30">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                  <h3 className="text-base font-semibold text-foreground">Executive Summary</h3>
                </div>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                  {briefingDoc.executiveSummary}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Background */}
          <section id="background">
            <div className="flex items-center gap-2 mb-3">
              <ScrollText className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-base font-semibold text-foreground">Background</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line pl-1">
              {briefingDoc.background}
            </p>
          </section>

          <Separator />

          {/* Key Findings */}
          <section id="key-findings">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-base font-semibold text-foreground">Key Findings</h3>
              <Badge variant="secondary" className="text-xs">
                {findingsCount}
              </Badge>
            </div>
            <div className="flex flex-col gap-3">
              {briefingDoc.keyFindings.map((finding, index) => {
                const config = IMPORTANCE_CONFIG[finding.importance];
                const isExpanded = expandedFindings.has(index);
                const hasDetails = !!finding.details;

                return (
                  <Card
                    key={index}
                    className={cn(
                      'transition-all duration-200',
                      config.border,
                      hasDetails && 'cursor-pointer hover:shadow-md'
                    )}
                    onClick={() => hasDetails && toggleFinding(index)}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <Badge variant={config.badge} className="text-[10px] h-5">
                              {config.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Finding {index + 1}
                            </span>
                          </div>
                          <p className="text-sm text-foreground leading-relaxed">
                            {finding.finding}
                          </p>
                        </div>
                        {hasDetails && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFinding(index);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>

                      {/* Expandable details */}
                      {hasDetails && (
                        <div
                          className={cn(
                            'overflow-hidden transition-all duration-300',
                            isExpanded ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'
                          )}
                        >
                          <div className="pt-2 border-t">
                            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                              {finding.details}
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <Separator />

          {/* Recommendations */}
          <section id="recommendations">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-base font-semibold text-foreground">Recommendations</h3>
              <Badge variant="secondary" className="text-xs">
                {recommendationsCount}
              </Badge>
            </div>
            <Card className="overflow-hidden bg-green-50/50 dark:bg-green-950/10 border-green-200/50 dark:border-green-800/20">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col gap-3">
                  {briefingDoc.recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex items-center justify-center h-6 w-6 rounded-full bg-green-100 dark:bg-green-900/30 shrink-0 mt-0.5">
                        <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground leading-relaxed">
                          <span className="font-medium text-muted-foreground mr-1.5">
                            {index + 1}.
                          </span>
                          {recommendation}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Next Steps */}
          {briefingDoc.nextSteps && briefingDoc.nextSteps.length > 0 && (
            <>
              <Separator />
              <section id="next-steps">
                <div className="flex items-center gap-2 mb-4">
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-base font-semibold text-foreground">Next Steps</h3>
                  <Badge variant="secondary" className="text-xs">
                    {nextStepsCount}
                  </Badge>
                </div>
                <Card className="overflow-hidden bg-blue-50/50 dark:bg-blue-950/10 border-blue-200/50 dark:border-blue-800/20">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col gap-3">
                      {briefingDoc.nextSteps.map((step, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900/30 shrink-0 mt-0.5">
                            <ArrowRight className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground leading-relaxed">
                              <span className="font-medium text-muted-foreground mr-1.5">
                                {index + 1}.
                              </span>
                              {step}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>
            </>
          )}

          {/* Appendix */}
          {briefingDoc.appendix && briefingDoc.appendix.length > 0 && (
            <>
              <Separator />
              <section id="appendix">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-base font-semibold text-foreground">Appendix</h3>
                </div>
                <div className="flex flex-col gap-2">
                  {briefingDoc.appendix.map((item, index) => {
                    const isOpen = expandedAppendices.has(index);
                    return (
                      <Collapsible
                        key={index}
                        open={isOpen}
                        onOpenChange={() => toggleAppendix(index)}
                      >
                        <Card>
                          <CollapsibleTrigger asChild>
                            <CardContent className="p-3 sm:p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <Hash className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium text-foreground">
                                    {item.title}
                                  </span>
                                </div>
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            </CardContent>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                              <Separator className="mb-3" />
                              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                                {item.content}
                              </p>
                            </div>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default BriefingDocViewer;
